import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";

interface Role {
  id: number;
  name: string;
  description: string;
  level: 'system' | 'organization' | 'team';
}

interface TeamPermissions {
  permissions: string[];
  roles: Role[];
}

interface PermissionContextType {
  permissions: string[];
  roles: Role[];
  teamPermissions: Record<number, TeamPermissions>;
  isLoading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasTeamPermission: (teamId: number, permission: string) => boolean;
  hasRole: (roleName: string) => boolean;
  hasTeamRole: (teamId: number, roleName: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType>({
  permissions: [],
  roles: [],
  teamPermissions: {},
  isLoading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  hasTeamPermission: () => false,
  hasRole: () => false,
  hasTeamRole: () => false,
  refreshPermissions: async () => {},
});

export const usePermission = () => useContext(PermissionContext);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, token } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [teamPermissions, setTeamPermissions] = useState<
    Record<number, TeamPermissions>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Track whether we've fetched for the current token to prevent duplicate calls
  const hasFetchedRef = useRef(false);
  // Use ref for token to avoid re-creating fetchPermissions on every token reference change
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchPermissions = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!isAuthenticated || !currentToken) {
      setPermissions([]);
      setRoles([]);
      setTeamPermissions({});
      // Don't set isLoading to false here - keep it true until we have a valid token
      // This prevents premature rendering of components that depend on permissions
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/me/permissions`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch permissions");
      }

      const data = await response.json();

      setPermissions(data.permissions || []);
      setRoles(data.roles || []);
      setTeamPermissions(data.teamPermissions || {});
    } catch (error) {
      setPermissions([]);
      setRoles([]);
      setTeamPermissions({});
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch permissions once when authenticated — uses stable deps to prevent refetch loops
  useEffect(() => {
    if (isAuthenticated && token) {
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        fetchPermissions();
      }
    } else {
      hasFetchedRef.current = false;
      setPermissions([]);
      setRoles([]);
      setTeamPermissions({});
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, !!token]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions: string[]): boolean => {
      return requiredPermissions.some((permission) =>
        permissions.includes(permission)
      );
    },
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions: string[]): boolean => {
      return requiredPermissions.every((permission) =>
        permissions.includes(permission)
      );
    },
    [permissions]
  );

  const hasTeamPermission = useCallback(
    (teamId: number, permission: string): boolean => {
      const teamPerms = teamPermissions[teamId];
      if (!teamPerms) return false;
      return teamPerms.permissions.includes(permission);
    },
    [teamPermissions]
  );

  const hasRole = useCallback(
    (roleName: string): boolean => {
      return roles.some((role) => role.name === roleName);
    },
    [roles]
  );

  const hasTeamRole = useCallback(
    (teamId: number, roleName: string): boolean => {
      const teamPerms = teamPermissions[teamId];
      if (!teamPerms) return false;
      return teamPerms.roles.some((role) => role.name === roleName);
    },
    [teamPermissions]
  );

  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  const value: PermissionContextType = {
    permissions,
    roles,
    teamPermissions,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasTeamPermission,
    hasRole,
    hasTeamRole,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
